import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { AddContactFormProps, InitialCreateContactFormState } from 'features/References';
import { ReferenceToSubmit } from 'features/References';
import { AddCTAButtons } from './components/AddCTAButtons';
import { ATSConnectHiringRequestCreateReferenceContactIds } from 'data-testids/ATS';
import { Box, Typography, useTheme } from '@mui/material';
import {
  createReferenceFormDefaultValues,
  newReferenceContactFields,
} from 'features/Connect/features/ConnectATSIntegration/data';
import { ErrorSnackbar } from 'sharedComponents/Snackbar';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';
import { referenceContactMethod, referenceStatus } from 'utils/typedEnums';
import { ReferenceField } from './components/ReferenceField';
import { useCreateReferenceContact } from 'features/References';
import auth from 'utils/auth';

export const AddContactForm: React.FC<AddContactFormProps> = ({
  candidateId,
  candidateName,
  handleSetNewContact,
  onCancel,
  onBackClick,
}) => {
  const [showErrorToast, setShowErrorToast] = useState(false);
  const theme = useTheme();
  const methods = useForm({
    defaultValues: createReferenceFormDefaultValues,
  });

  const {
    mutate: createReference,
    error,
    isLoading,
  } = useCreateReferenceContact(candidateId, {
    onSuccess: (data) => {
      handleSetNewContact(data);
      onBackClick(data);
    },
    onError: () => setShowErrorToast(true),
  });
  const errorMessage = getAxiosErrorMessageByStatusCode(error);

  const handleAddSubmit = (formState: InitialCreateContactFormState) => {
    const status = referenceStatus.not_started;
    const contact_type = referenceContactMethod.none;
    const districtId = auth.getUser().profile.district.id;

    const formStateToSubmit: ReferenceToSubmit = {
      ...formState,
      status,
      contact_type,
      candidate: candidateId,
      district: districtId,
      candidate_updated_last: false,
      candidate_updated_when: null,
    };

    createReference({ payload: formStateToSubmit }, districtId);
  };

  const submitShouldBeDisabled = isLoading || showErrorToast;

  return (
    <Box
      data-testid={
        ATSConnectHiringRequestCreateReferenceContactIds.ADD_NEW_REFERENCE_CONTACT_FORM_SECTION
      }
    >
      <Typography
        variant="h2"
        sx={{ lineHeight: 2.2 }}
        data-testid={
          ATSConnectHiringRequestCreateReferenceContactIds.ADD_NEW_REFERENCE_CONTACT_FORM_TITLE
        }
      >
        Add new reference contact for {candidateName}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: theme.typography.fontWeightMedium, pb: 2 }}>
        New reference contact
      </Typography>
      <FormProvider {...methods}>
        <form>
          {newReferenceContactFields.map((field, i) => (
            <ReferenceField
              fieldName={field.fieldName}
              placeholder={field.placeholder}
              isRequired={field.isRequired}
              formId={field.formId}
              pattern={field.pattern}
              key={i}
            />
          ))}
          <AddCTAButtons
            onCancel={onCancel}
            onSave={handleAddSubmit}
            shouldBeDisabled={submitShouldBeDisabled}
          />
        </form>
      </FormProvider>
      <ErrorSnackbar
        dataTestId={ATSConnectHiringRequestCreateReferenceContactIds.ERROR_TOAST}
        message={errorMessage}
        onClose={() => setShowErrorToast(false)}
        open={showErrorToast}
      />
    </Box>
  );
};
